import React, { useEffect, useState } from 'react';
import { Megaphone } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import opsService from '../../../services/opsService';

function Card({ label, value, sub }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
      <div className="text-ink-muted text-small">{label}</div>
      <div className="text-2xl font-bold text-ink-soft">{value}</div>
      {sub && <div className="text-ink-muted text-small mt-1">{sub}</div>}
    </div>
  );
}

const num = (n) => Number(n || 0).toLocaleString();
const money = (n, cur) => `${cur} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => (n == null ? '—' : `${(n * 100).toFixed(2)}%`);
const dec = (n) => (n == null ? '—' : Number(n).toFixed(2));
const PLATFORM_LABEL = { meta: 'Meta', google: 'Google Ads' };

export default function OpsAds() {
  const [data, setData] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    opsService.adsSummary().then(setData).catch((e) =>
      setError(e?.response?.data?.detail || 'Failed to load ads'));
    opsService.adsSources().then(setSources).catch(() => setSources([]));
  }, []);

  if (error) return <div className="text-danger p-6">{error}</div>;
  if (!data) return <div className="text-ink-muted p-6">Loading ads…</div>;

  const primary = data.totals?.[0];
  const cur = primary?.currency || 'USD';
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-ink-soft flex items-center gap-2">
          <Megaphone className="w-5 h-5" /> Ads Monitoring
        </h3>
        <div className="text-ink-muted text-small">
          {sources.length === 0 ? 'No data yet — refresh via Claude' :
            'Last updated: ' + sources.map((s) => `${s.platform} ${s.last_spend_date || '—'}`).join(' · ') + ' · refresh via Claude'}
        </div>
      </div>

      {data.by_platform.length === 0 && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6 text-ink-muted">
          No ad spend imported yet. Ask Claude to pull your Meta &amp; Google numbers.
        </div>
      )}

      {data.by_platform.map((p) => (
        <div key={`${p.platform}-${p.currency}`} className="space-y-3">
          <div className="text-ink-soft font-medium">
            {PLATFORM_LABEL[p.platform] || p.platform} <span className="text-ink-muted font-normal">({p.currency})</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Spend" value={money(p.spend, p.currency)} />
            <Card label="Impressions" value={num(p.impressions)} sub={`CPM ${dec(p.cpm)}`} />
            <Card label="Clicks" value={num(p.clicks)} sub={`CTR ${pct(p.ctr)} · CPC ${dec(p.cpc)}`} />
            <Card label="Conversions" value={num(p.conversions)} sub={`Cost/conv ${dec(p.cost_per_conversion)}`} />
          </div>
        </div>
      ))}

      {data.by_date.length > 0 && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
          <div className="font-medium text-ink-soft mb-2">Spend over time</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.by_date}>
              <XAxis dataKey="date" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => money(v, cur)} />
              <Bar dataKey="spend" fill="#7c3aed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.by_campaign.length > 0 && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
          <div className="px-4 py-2 font-medium text-ink-soft border-b">By campaign</div>
          <table className="w-full text-small">
            <thead className="bg-surface-muted text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2">Campaign</th><th className="text-left px-4 py-2">Platform</th>
                <th className="text-right px-4 py-2">Spend</th><th className="text-right px-4 py-2">Clicks</th>
                <th className="text-right px-4 py-2">CTR</th>
              </tr>
            </thead>
            <tbody>
              {data.by_campaign.map((c, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2 text-ink-soft">{c.campaign}</td>
                  <td className="px-4 py-2">{c.platform}</td>
                  <td className="px-4 py-2 text-right">{money(c.spend, c.currency)}</td>
                  <td className="px-4 py-2 text-right">{num(c.clicks)}</td>
                  <td className="px-4 py-2 text-right">{pct(c.ctr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
